// ---------------------------------------------------------------------------
// Web-only authentication gate. A browser build WITH cloud sync enabled
// (VITE_CLOUD_SYNC_ENABLED=true — the hosted portal) locks the whole app
// behind sign-in: unauthenticated visitors see only this screen. A browser
// build without cloud config (plain `npm run dev`) and the Tauri desktop build
// stay local-first and never render it (see requiresAuthGate). Sign-up and
// sign-in use the same CIAM flow as the account menu; once signed in,
// WebLinkPrompt links this browser to the cloud account automatically.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, Typography, keyframes } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { signIn, signUp, useAuth, type AuthState } from '../auth/auth';
import BrandLogo, { BrandMark } from './BrandLogo';
import { cloudConfig } from '../config/cloud';
import { autoLinkForWeb, isLinked } from '../sync/controller';
import { useAppStore } from '../store/useAppStore';

/**
 * True when the UI must be replaced by the sign-in gate: web (non-Tauri)
 * visitors of a cloud-enabled build who have not finished signing in. The
 * desktop app is never gated, and neither is a local browser build without
 * cloud sync configured (there is no account system to sign in to).
 */
export function requiresAuthGate(tauri: boolean, cloudEnabled: boolean, authState: AuthState): boolean {
  return !tauri && cloudEnabled && authState !== 'signed-in';
}

// Slow ambient drift for the decorative shapes on the brand panel.
const float1 = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(24px, -30px) rotate(8deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;
const float2 = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-20px, 24px) scale(1.08); }
  100% { transform: translate(0, 0) scale(1); }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SELLING_POINTS = [
  'Real interview questions researched per school',
  'AI-coached answers built from your application',
  'Mock interviews with structured, honest scoring',
  'Progress that syncs across all your devices',
];

/** Decorative left panel: brand gradient, tagline, and feature highlights. */
function BrandPanel() {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
        background: 'linear-gradient(135deg, #152a47 0%, #1f3a5f 45%, #2a9d8f 130%)',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        flex: '1 1 55%',
        p: { md: 8, lg: 10 },
      }}
    >
      {/* Ambient shapes */}
      <Box sx={{ position: 'absolute', top: '-12%', right: '-8%', width: 380, height: 380, borderRadius: '50%', bgcolor: 'rgba(42,157,143,0.25)', filter: 'blur(2px)', animation: `${float1} 14s ease-in-out infinite` }} />
      <Box sx={{ position: 'absolute', bottom: '-15%', left: '-10%', width: 420, height: 420, borderRadius: '50%', bgcolor: 'rgba(143,179,217,0.14)', animation: `${float2} 18s ease-in-out infinite` }} />
      <Box sx={{ position: 'absolute', top: '30%', left: '55%', width: 140, height: 140, borderRadius: 6, bgcolor: 'rgba(255,255,255,0.05)', transform: 'rotate(20deg)', animation: `${float1} 22s ease-in-out infinite` }} />

      <Stack spacing={4} sx={{ position: 'relative', maxWidth: 480, animation: `${fadeUp} 0.7s ease-out` }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BrandMark size={44} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
            Dental Interview Coach
          </Typography>
        </Stack>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, fontSize: { md: '2.2rem', lg: '2.6rem' } }}>
            Walk into your dental school interview ready.
          </Typography>
          <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.75)', fontSize: 17, lineHeight: 1.6 }}>
            Practice with real questions, get coaching grounded in your own application, and track
            your readiness school by school.
          </Typography>
        </Box>
        <Stack spacing={1.5}>
          {SELLING_POINTS.map((p, i) => (
            <Stack
              key={p}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ animation: `${fadeUp} 0.6s ease-out ${0.15 + i * 0.1}s both` }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#9be8dd' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>{p}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

export default function AuthGate() {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doAuth(kind: 'in' | 'up') {
    setBusy(true);
    setError(null);
    try {
      await (kind === 'in' ? signIn() : signUp());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const initializing = auth.state === 'initializing';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <BrandPanel />

      {/* Auth column */}
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 45%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 6 },
        }}
      >
        <Stack spacing={3} sx={{ width: '100%', maxWidth: 380, animation: `${fadeUp} 0.5s ease-out` }}>
          {/* Compact brand header for phones (the panel is hidden there). */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <BrandLogo size={40} nameSx={{ fontSize: '1.2rem' }} />
          </Box>

          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Welcome
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Sign in to your prep portal — your questions, answers, and progress stay in sync on
              every device.
            </Typography>
          </Box>

          {error && <Alert severity="error">Sign-in failed: {error}</Alert>}

          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              disabled={busy || initializing}
              onClick={() => void doAuth('in')}
            >
              {initializing ? 'Checking session…' : 'Sign in'}
            </Button>
            <Divider sx={{ color: 'text.secondary', fontSize: 13 }}>new here?</Divider>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<PersonAddAltIcon />}
              disabled={busy || initializing}
              onClick={() => void doAuth('up')}
            >
              Create your free account
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            A secure Microsoft-powered window opens to sign you in. Free accounts include monthly AI
            credits — no card required.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

/**
 * Web companion to the gate: on the hosted portal the cloud account is primary
 * and sync is always on, so once the gate signs a user in this links the
 * browser automatically and silently — there is no merge-vs-use-cloud choice on
 * the web (that dialog is a desktop concern, where the app is offline-first).
 * If the backend is unreachable the link is retried with backoff; the app keeps
 * working locally meanwhile and pushes everything up once linking succeeds.
 */
export function WebLinkPrompt() {
  const auth = useAuth();
  const cloudEnabled = cloudConfig.syncEnabled;
  const showToast = useAppStore((s) => s.showToast);
  const attempted = useRef(false);

  useEffect(() => {
    if (!cloudEnabled || auth.state !== 'signed-in' || isLinked() || attempted.current) return;
    attempted.current = true;
    let cancelled = false;
    void (async () => {
      let warned = false;
      for (let delay = 2_000; !cancelled && !isLinked(); delay = Math.min(delay * 2, 60_000)) {
        try {
          await autoLinkForWeb();
          return;
        } catch (e) {
          if (!warned) {
            warned = true;
            showToast(`Could not start sync yet (${(e as Error).message}); retrying…`, 'info');
          }
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cloudEnabled, auth.state, showToast]);

  return null;
}
